import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Plane, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { fmtData, fmtEuro } from "../lib/helpers";

const TIPI_SPESA = ["Viaggio", "Vitto", "Alloggio", "Pedaggi", "Parcheggio", "Materiali", "Altro"];

const EMPTY = {
  dipendente_id: "", data: "", destinazione: "", motivo: "",
  commessa_id: "", spese: [], note: "", rimborsata: false,
};

function TrasfertaForm({ open, onClose, onSaved, trasferta, dipendenti, commesse }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(trasferta
        ? { ...EMPTY, ...trasferta, commessa_id: trasferta.commessa_id || "" }
        : EMPTY);
    }
  }, [open, trasferta]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpesa = (i, k, v) => setForm((f) => ({ ...f, spese: f.spese.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const addSpesa = () => setForm((f) => ({ ...f, spese: [...f.spese, { tipo: "Viaggio", descrizione: "", importo: "" }] }));
  const delSpesa = (i) => setForm((f) => ({ ...f, spese: f.spese.filter((_, j) => j !== i) }));
  const totale = form.spese.reduce((acc, s) => acc + (Number(s.importo) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      commessa_id: form.commessa_id || null,
      data: form.data || null,
      spese: form.spese.map((s) => ({ ...s, importo: Number(s.importo) || 0 })),
    };
    try {
      if (trasferta) await api.put(`/trasferte/${trasferta.id}`, payload);
      else await api.post("/trasferte", payload);
      toast.success(trasferta ? "Trasferta aggiornata" : "Trasferta registrata");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trasferta-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{trasferta ? "Modifica trasferta" : "Nuova trasferta"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Dipendente *</label>
            <Select value={form.dipendente_id} onValueChange={(v) => set("dipendente_id", v)} required>
              <SelectTrigger data-testid="trasferta-form-dipendente"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Data trasferta</label>
            <input type="date" data-testid="trasferta-form-data" value={form.data || ""} onChange={(e) => set("data", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Destinazione</label>
            <input data-testid="trasferta-form-destinazione" value={form.destinazione} onChange={(e) => set("destinazione", e.target.value)} className={inputCls} placeholder="es. Cantiere Monza" />
          </div>
          <div>
            <label className={labelCls}>Motivo</label>
            <input data-testid="trasferta-form-motivo" value={form.motivo} onChange={(e) => set("motivo", e.target.value)} className={inputCls} placeholder="es. Sopralluogo, sopralluogo DL" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Commessa collegata (facoltativo)</label>
            <Select value={form.commessa_id} onValueChange={(v) => set("commessa_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="trasferta-form-commessa"><SelectValue placeholder="Nessuna" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Nessuna</SelectItem>
                {commesse.map((c) => <SelectItem key={c.id} value={c.id}>{c.codice} — {c.denominazione}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spese sostenute</label>
              <button type="button" onClick={addSpesa} data-testid="trasferta-add-spesa" className="flex items-center gap-1 text-xs font-semibold text-blue-900 hover:underline">
                <Plus className="h-3.5 w-3.5" /> Aggiungi spesa
              </button>
            </div>
            <div className="space-y-2">
              {form.spese.length === 0 && <div className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-md px-3 py-3">Nessuna spesa inserita.</div>}
              {form.spese.map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`spesa-row-${i}`}>
                  <Select value={s.tipo} onValueChange={(v) => setSpesa(i, "tipo", v)}>
                    <SelectTrigger data-testid={`spesa-tipo-${i}`} className="w-32 h-9 text-sm bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPI_SPESA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <input data-testid={`spesa-descrizione-${i}`} value={s.descrizione} onChange={(e) => setSpesa(i, "descrizione", e.target.value)}
                    placeholder="Descrizione" className="flex-1 px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30" />
                  <input type="number" step="0.01" min="0" data-testid={`spesa-importo-${i}`} value={s.importo} onChange={(e) => setSpesa(i, "importo", e.target.value)}
                    placeholder="€" className="w-28 px-3 py-2 text-sm font-code bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30" />
                  <button type="button" onClick={() => delSpesa(i)} data-testid={`spesa-delete-${i}`} className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-700"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="text-right mt-2 text-sm">
              Totale: <span className="font-code font-semibold text-slate-800" data-testid="trasferta-totale-form">{fmtEuro(totale)}</span>
            </div>
          </div>

          <div className="col-span-2">
            <label className={labelCls}>Note</label>
            <textarea rows={2} data-testid="trasferta-form-note" value={form.note} onChange={(e) => set("note", e.target.value)} className={inputCls} />
          </div>
          <div className="col-span-2 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="trasferta-form-rimborsata" checked={form.rimborsata} onChange={(e) => set("rimborsata", e.target.checked)} className="h-4 w-4 accent-blue-900" />
              Spese già rimborsate al dipendente
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} data-testid="trasferta-form-cancel" className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50">Annulla</button>
              <button type="submit" disabled={saving || !form.dipendente_id} data-testid="trasferta-form-submit" className="px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
                {saving ? "Salvataggio…" : "Salva trasferta"}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Trasferte() {
  const [trasferte, setTrasferte] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroDipendente, setFiltroDipendente] = useState("tutti");
  const [filtroRimborso, setFiltroRimborso] = useState("tutte");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, d, c] = await Promise.all([api.get("/trasferte"), api.get("/dipendenti"), api.get("/commesse")]);
      setTrasferte(t.data);
      setDipendenti(d.data);
      setCommesse(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nome = (id) => {
    const d = dipendenti.find((x) => x.id === id);
    return d ? `${d.nome} ${d.cognome}` : "—";
  };

  const codiceCommessa = (id) => {
    const c = commesse.find((x) => x.id === id);
    return c ? c.codice : null;
  };

  const filtrate = useMemo(() => trasferte.filter((t) => {
    if (filtroDipendente !== "tutti" && t.dipendente_id !== filtroDipendente) return false;
    if (filtroRimborso === "da_rimborsare" && t.rimborsata) return false;
    if (filtroRimborso === "rimborsate" && !t.rimborsata) return false;
    return true;
  }), [trasferte, filtroDipendente, filtroRimborso]);

  const meseCorrente = new Date().toISOString().slice(0, 7);
  const totaleMese = trasferte.filter((t) => (t.data || "").startsWith(meseCorrente)).reduce((a, t) => a + (t.totale || 0), 0);
  const totaleDaRimborsare = trasferte.filter((t) => !t.rimborsata).reduce((a, t) => a + (t.totale || 0), 0);

  const elimina = async (t) => {
    if (!window.confirm(`Eliminare la trasferta di ${nome(t.dipendente_id)} del ${fmtData(t.data)}?`)) return;
    try {
      await api.delete(`/trasferte/${t.id}`);
      toast.success("Trasferta eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleRimborso = async (t) => {
    try {
      await api.put(`/trasferte/${t.id}`, { ...t, rimborsata: !t.rimborsata });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="trasferte-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">Trasferte & spese</h1>
          <p className="text-sm text-slate-500 mt-1">Gestione trasferte dei dipendenti e relative spese sostenute. Visibile solo agli amministratori.</p>
        </div>
        <button data-testid="trasferte-add-trigger" onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950">
          <Plus className="h-4 w-4" /> Nuova trasferta
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="trasferte-kpi-mese">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spese mese corrente</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900 font-code">{fmtEuro(totaleMese)}</div>
        </div>
        <div className="bg-white border border-amber-300 bg-amber-50 rounded-md p-4" data-testid="trasferte-kpi-da-rimborsare">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">Da rimborsare</div>
          <div className="mt-1 font-display text-2xl font-bold text-amber-900 font-code">{fmtEuro(totaleDaRimborsare)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="trasferte-kpi-numero">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trasferte registrate</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{trasferte.length}</div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-md px-4 py-3">
        <Select value={filtroDipendente} onValueChange={setFiltroDipendente}>
          <SelectTrigger data-testid="trasferte-filter-dipendente" className="w-52 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i dipendenti</SelectItem>
            {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroRimborso} onValueChange={setFiltroRimborso}>
          <SelectTrigger data-testid="trasferte-filter-rimborso" className="w-48 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte</SelectItem>
            <SelectItem value="da_rimborsare">Da rimborsare</SelectItem>
            <SelectItem value="rimborsate">Rimborsate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500 text-sm" data-testid="trasferte-loading">Caricamento trasferte…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="trasferte-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Data", "Dipendente", "Destinazione / motivo", "Commessa", "Dettaglio spese", "Totale", "Rimborso", ""].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrate.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                  <Plane className="h-6 w-6 mx-auto mb-2 text-slate-300" />Nessuna trasferta registrata.
                </td></tr>
              )}
              {filtrate.map((t) => (
                <tr key={t.id} data-testid={`trasferta-row-${t.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70">
                  <td className="px-3 py-2.5 font-code text-xs text-slate-600 whitespace-nowrap">{fmtData(t.data)}</td>
                  <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap">{nome(t.dipendente_id)}</td>
                  <td className="px-3 py-2.5 max-w-56">
                    <div className="text-slate-800 truncate">{t.destinazione || "—"}</div>
                    <div className="text-xs text-slate-500 truncate">{t.motivo}</div>
                  </td>
                  <td className="px-3 py-2.5 font-code text-xs text-slate-600">{codiceCommessa(t.commessa_id) || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 max-w-64">
                    {(t.spese || []).map((s, i) => (
                      <div key={i} className="flex justify-between gap-3">
                        <span className="truncate">{s.tipo}{s.descrizione ? ` — ${s.descrizione}` : ""}</span>
                        <span className="font-code shrink-0">{fmtEuro(s.importo)}</span>
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2.5 font-code text-sm font-semibold text-slate-800 whitespace-nowrap">{fmtEuro(t.totale || 0)}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => toggleRimborso(t)} data-testid={`trasferta-rimborso-${t.id}`}>
                      <Badge variant="outline" className={t.rimborsata ? "bg-green-100 text-green-800 border-green-200 cursor-pointer" : "bg-amber-100 text-amber-800 border-amber-200 cursor-pointer"}>
                        {t.rimborsata ? "Rimborsata" : "Da rimborsare"}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right">
                    <button data-testid={`trasferta-edit-${t.id}`} onClick={() => { setEditing(t); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`trasferta-delete-${t.id}`} onClick={() => elimina(t)} className="p-1.5 rounded hover:bg-red-100 text-slate-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TrasfertaForm open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} trasferta={editing} dipendenti={dipendenti} commesse={commesse} />
    </div>
  );
}
